/**
 * Putting ONE kitchen prop on a counter, or on a range's handle.
 *
 * Counters reuse the bathroom's surface machinery (`surfaces.js`): its free
 * runs (occupiers, voids, padding, physical-end margins) and its depth seat.
 * What a kitchen adds is WHERE along a run a prop goes, which the bathroom's
 * composed row never had to choose: beside the sink, at the range's landing,
 * at the far end of the run, off-centre on an island. Those are `prefer`
 * functions over the feasible run position, so one placer serves them all.
 */

const { stagingPropDimsM } = require('../../propDims');
const { footprintFor } = require('../../../geometry/objectFootprint');
const { objectWorldFromLocal } = require('../../../dorm-styling/plan/shared');
const { rangeFrontLayout } = require('../../../kitchen-assembly/rangeFront');
const { teaTowelBox } = require('../../teaTowel');
const {
  freeRunsOnHost,
  usableBounds,
  depthOffset,
  planDims,
} = require('../surfaces');
const { isKitchenVoid, headroomBlocks } = require('./keepOuts');

const IN = 0.0254;
/** A prop must leave at least this much of the counter's depth spare. */
const DEPTH_MARGIN_M = 0.5 * IN;
/** An object whose underside is more than this above the counter is overhead, not on it. */
const OVERHEAD_MIN_GAP_M = 1 * IN;

/**
 * The feasible spans for a prop's CENTRE on a counter: free runs after the
 * occupiers, voids and `blocked` stretches, minus the edge margins, minus the
 * prop's own half-width.
 */
function feasibleSpans(counter, dims, scanObjects, blocked, depth) {
  const host = counter.host;
  const hd = host.dimensions;
  // The prop has to fit the counter's DEPTH too. The corpus has a 12 in bar
  // ledge, a 2 in strip and a 1 in scan artifact typed as countertops; a
  // 12 in tulip arrangement "on" them hangs off the edge.
  if (dims.z > hd.z - DEPTH_MARGIN_M) return [];
  const corners = footprintFor(host.center, hd.x, hd.z, host.rotation?.y || 0);
  if (!corners) return [];
  const lz = depthOffset(depth, hd.z, dims.z);
  const allBlocked = [
    ...blocked,
    ...headroomBlocks(counter, dims.y, lz - dims.z / 2, lz + dims.z / 2),
  ];
  // Anything whose underside is above the counter is OVERHEAD, and the
  // headroom blocks above already account for it. Left in, the 40 corpus
  // uppers that are not wall-parented read as standing ON the counter and
  // erased whole kitchens' worth of counter (one 133 in run, end to end).
  const standingOrVoid = scanObjects.filter((o) => {
    const h = o?.dimensions?.y;
    if (!o?.center || !Number.isFinite(h)) return true;
    return o.center.y - h / 2 <= counter.top + OVERHEAD_MIN_GAP_M;
  });
  const zones = freeRunsOnHost(
    host,
    corners,
    counter.top,
    standingOrVoid,
    counter.hostW,
    {
      isVoid: isKitchenVoid,
      blocked: allBlocked,
    }
  );
  const spans = [];
  for (const z of zones) {
    const { lo, hi } = usableBounds(z, counter.hostW);
    const a = lo + dims.x / 2;
    const b = hi - dims.x / 2;
    if (b >= a) spans.push({ lo: a, hi: b });
  }
  return spans;
}

const SPREAD_STEP_M = 1 * IN;

/** Plan distance from a world point to the nearest of `points`. */
const clearanceFrom = (p, points) =>
  Math.min(...points.map((q) => Math.hypot(p.x - q.x, p.z - q.z)));

/** The run position on span `s` farthest from the nearest of `points`, and that distance. */
function farthestOnSpan(counter, s, points) {
  let best = null;
  const n = Math.max(1, Math.ceil((s.hi - s.lo) / SPREAD_STEP_M));
  for (let i = 0; i <= n; i++) {
    const t = s.lo + ((s.hi - s.lo) * i) / n;
    const p = objectWorldFromLocal(counter.host, t - counter.hostW / 2, 0);
    const score = clearanceFrom(p, points);
    if (!best || score > best.score) best = { t, score };
  }
  return best;
}

/**
 * Place one prop on a counter.
 *
 * @param {Object} counter  from `readKitchen`
 * @param {Object} wish     the programme line
 * @param {Object[]} scanObjects  the room's objects plus props already placed
 * @param {Function} make   (wish, fields) => entry
 * @param {Object} opts
 * @param {Array<{start,end}>} [opts.blocked]  run stretches to avoid (prep zone)
 * @param {string} [opts.depth]  see `depthOffset`
 * @param {number} [opts.towardT]  prefer the feasible position nearest this run position
 * @param {number} [opts.maxFromT]  ...and refuse one farther than this from it
 * @param {number} [opts.awayFromT]  prefer the feasible position farthest from this
 * @param {Array<{x,z}>} [opts.farFrom]  prefer the position farthest (in plan)
 *   from the nearest of these points — how a free-standing prop spreads away
 *   from the props already out instead of piling onto one run
 * @returns {Object|null} the minted entry
 */
function placeOnCounter(counter, wish, scanObjects, make, opts = {}) {
  const dims = stagingPropDimsM(wish.type);
  if (!dims) return null;
  const spans = feasibleSpans(
    counter,
    dims,
    scanObjects,
    opts.blocked || [],
    opts.depth
  );
  if (!spans.length) return null;
  const clamp = (t, s) => Math.min(Math.max(t, s.lo), s.hi);
  let best = null;
  for (const s of spans) {
    let t;
    let score;
    if (Number.isFinite(opts.towardT)) {
      t = clamp(opts.towardT, s);
      score = -Math.abs(t - opts.towardT);
    } else if (opts.farFrom?.length) {
      ({ t, score } = farthestOnSpan(counter, s, opts.farFrom));
    } else if (Number.isFinite(opts.awayFromT)) {
      t =
        Math.abs(s.lo - opts.awayFromT) > Math.abs(s.hi - opts.awayFromT)
          ? s.lo
          : s.hi;
      score = Math.abs(t - opts.awayFromT);
    } else {
      // No preference: the middle of the roomiest span.
      t = (s.lo + s.hi) / 2;
      score = s.hi - s.lo;
    }
    if (!best || score > best.score) best = { t, score };
  }
  if (Number.isFinite(opts.towardT) && Number.isFinite(opts.maxFromT)) {
    if (Math.abs(best.t - opts.towardT) > opts.maxFromT) return null;
  }
  const host = counter.host;
  const lz = depthOffset(opts.depth, host.dimensions.z, dims.z);
  const { x, z } = objectWorldFromLocal(host, best.t - counter.hostW / 2, lz);
  return make(wish, {
    center: { x, y: counter.top + dims.y / 2, z },
    dimensions: dims,
    rotation: { x: 0, y: host.rotation?.y || 0, z: 0 },
    host: counter.obj,
  });
}

/**
 * Hang the tea towel over a procedural range's oven handle. The drape's
 * handle line (`teaTowelBox().barFromCentre`) is put on the handle the range
 * draws (`rangeFrontLayout`), right of centre the way a towel is hung.
 * A pro range (two ovens, two handles) is skipped: it has no single handle to
 * hang from, and no corpus range is one.
 */
function placeOnRangeHandle(range, wish, make) {
  // A range of unknown size or height has no handle we can find: no towel,
  // rather than one at NaN.
  const d = planDims(range);
  if (![d.x, d.y, d.z].every((v) => v > 0) || !Number.isFinite(range.center?.y))
    return null;
  const front = rangeFrontLayout({ width: d.x, height: d.y, depth: d.z });
  if (front.handles.length !== 1) return null;
  const handle = front.handles[0];
  const dims = stagingPropDimsM(wish.type);
  const { barFromCentre } = teaTowelBox();
  const lx = handle.x + handle.length * 0.22;
  const lz = handle.z - barFromCentre.z * IN;
  const { x, z } = objectWorldFromLocal(range, lx, lz);
  return make(wish, {
    center: { x, y: range.center.y + handle.y - barFromCentre.y * IN, z },
    dimensions: dims,
    rotation: { x: 0, y: range.rotation?.y || 0, z: 0 },
    host: range,
  });
}

module.exports = {
  placeOnCounter,
  placeOnRangeHandle,
  feasibleSpans,
  clearanceFrom,
};
